import React from 'react';

export const PERMISSION_MAP = {
  analytics: {
    label: 'Analytics',
    isCorpOnly: true,
    disabledCorp: {
      admin: ['read'],
      nonAdmin: ['read']
    }
  },
  kids: {
    label: 'Students',
    disabled: {
      admin: ['none'],
      nonAdmin: ['none']
    }
  },
  kids_addresses: {
    label: 'Student Address',
    disabled: {
      admin: [],
      nonAdmin: []
    }
  },
  sections: {
    label: 'Rooms',
    disabled: {
      admin: ['none'],
      nonAdmin: ['none']
    },
    disabledCorp: {
      admin: ['none'],
      nonAdmin: ['none']
    }
  },
  programs: {
    label: 'Programs',
    featureFlag: 'enable_programs',
    enabledBySchoolSetting: school => school.enable_programs
  },
  carers: {
    label: 'Parents/Pickup',
    disabled: {
      admin: ['none'],
      nonAdmin: ['none']
    }
  },
  carers_info: {
    label: 'Parent Contact',
    disabled: {
      admin: [],
      nonAdmin: []
    },
    tooltip: 'Email & Phone contact of parents'
  },
  teachers: {
    label: 'Staff',
    disabled: {
      nonAdmin: ['none', 'read', 'write']
    }
  },
  teachers_scheduling: {
    label: 'Staff Scheduling',
    disabled: {
      admin: [],
      nonAdmin: []
    },
    featureFlag: 'staff_schedule_enabled',
    enabledBySchoolSetting: school => school.staff_schedule_enabled
  },
  billing: { label: 'Billing' },
  accounting_total: {
    label: 'Accounting Total',
    disabled: {
      admin: ['read'],
      nonAdmin: ['read']
    }
  },
  teachers_siso: {
    label: 'Staff Timecard'
  },
  teachers_messaging: {
    label: 'Staff Messaging',
    disabled: {
      admin: [],
      nonAdmin: []
    }
  },
  newsletters: {
    label: 'Newsletters'
  },
  meals: {
    label: 'Meals',
    disabled: {
      admin: ['none'],
      nonAdmin: ['none']
    }
  },
  assessments: {
    label: 'Assessments'
  },
  room_visibility: {
    label: 'Room Visibility',
    tooltip:
      'Staff with Full Access to Room Visibility can see all rooms in the center. Staff with No Access can only see their assigned rooms. This can also be edited when assigning rooms to staff.',
    disabled: {
      admin: ['none', 'read'],
      nonAdmin: ['read']
    },
    featureFlag: 'show-room-vis-permission',
    showPermission: 'show-room-vis-permission'
  },
  center_te_reports: {
    label: 'Reports - Tuition Express',
    disabled: {
      admin: ['read'],
      nonAdmin: ['read']
    },
    enabledBySchoolSetting: school => school.billing_gateway === 'te',
    showPermission: 'online-ent-showtereportspermissions'
  },
  // parent_relation: {
  //   label: 'Parent Relation',
  //   disabled: {
  //     admin: [],
  //     nonAdmin: []
  //   }
  // },
  corp_te_reports: {
    label: 'Reports - Corporate Tuition Express',
    disabled: {
      admin: ['read'],
      nonAdmin: ['read']
    },
    enabledBySchoolSetting: school => school.billing_gateway === 'te',
    showPermission: 'online-ent-showtereportspermissions'
  },
  parents_messaging: {
    label: 'Parent/Staff Messaging',
    disabled: {
      admin: ['read'],
      nonAdmin: ['read']
    },
    tooltip:
      'Staff with Full Access to Staff/Parent Messaging will be limited to see messages in their Primary and Additional rooms'
  },
  lead_crm: { label: 'Leads' },
  documents: {
    label: 'Documents',
    disabledCorp: {
      admin: ['read'],
      nonAdmin: ['read']
    }
  },
  school_calendars: {
    label: 'Calendar',
    disabled: {
      nonAdmin: []
    }
  },
  reports: {
    label: 'Reports',
    disabled: {
      nonAdmin: ['read'],
      admin: ['read']
    },
    disabledCorp: {
      admin: ['read'],
      nonAdmin: ['read']
    }
  },
  school_settings: {
    label: 'School Settings',
    disabled: {
      nonAdmin: ['none', 'read', 'write']
    }
  },
  school_access: {
    label: 'School Access',
    disabledCorp: {
      admin: ['none'],
      nonAdmin: ['none']
    }
  },
  roles_and_permissions: {
    label: 'Roles & Permissions',
    disabled: {
      admin: [],
      nonAdmin: []
    },
    dependsOn: ['teachers'],
    showPermission: 'custom-permissions-roles',
    tooltip: (
      <div style={{ width: '550px' }}>
        Access to Roles & Permissions is based on your Staff Permission settings:
        <ul className="bullet-list">
          <li>
            <strong>Full Staff Access</strong> &rarr; Roles & Permissions can be <strong>Full</strong>,{' '}
            <strong>Read</strong>, or <strong>No Access</strong>.
          </li>
          <li>
            <strong>Read Staff Access</strong> &rarr; Roles & Permissions can only be <strong>Read</strong> or{' '}
            <strong>No Access</strong>.
          </li>
          <li>
            <strong>No Staff Access</strong> &rarr; Roles & Permissions is always <strong>No Access</strong>.
          </li>
        </ul>
      </div>
    )
  }
  // subscription: {
  //   label: 'Subscription',
  //   disabled: {
  //     nonAdmin: ['none', 'read', 'write']
  //   }
  // }
};

export const PERMISSION_VALUES = {
  none: 'No Access',
  read: 'Read',
  write: 'Full Access'
};

export const PERMISSIONS = Object.keys(PERMISSION_MAP).filter(p => !PERMISSION_MAP[p].isCorpOnly);

export const CORP_PERMISSIONS = [
  'analytics',
  'sections',
  'teachers',
  'billing',
  'lead_crm',
  'reports',
  'documents',
  'school_access'
];

export const SCHOOL_PERMISSIONS = ['school_access'];

export function getPermissionMap(flags) {
  const map = { ...PERMISSION_MAP };

  if (flags['show-room-vis-permission']) {
    map['sections'] = {
      ...map['sections'],
      label: 'Room Edit',
      tooltip:
        'Staff with Full Access to Room Edit can modify classroom details and create new rooms. Staff with read access can only view room information.'
    };
  }

  return map;
}

export const ADMIN_DEFAULTS = {
  kids: ['write'],
  kids_addresses: ['write'],
  sections: ['write'],
  carers: ['write'],
  carers_info: ['write'],
  teachers: ['write'],
  teachers_siso: ['write'],
  billing: ['write'],
  lead_crm: ['write'],
  lead_crm_states: ['write'],
  reports: ['write'],
  programs: ['write'],
  school_settings: ['write'],
  custom_attributes: ['write'],
  subscription: ['write'],
  teachers_messaging: ['write'],
  school_calendars: ['write'],
  parents_messaging: ['write'],
  documents: ['write'],
  accounting_total: ['write'],
  teachers_scheduling: ['write'],
  parent_relation: ['write'],
  newsletters: ['write'],
  subsidies: ['write'],
  meals: ['write'],
  assessments: ['write'],
  analytics: ['write'],
  newsletter_templates: ['write'],
  room_visibility: ['write'],
  center_te_reports: ['write'],
  corp_te_reports: ['none'],
  roles_and_permissions: ['write']
};

export const NON_ADMIN_DEFAULTS = {
  kids: ['write'],
  kids_addresses: ['write'],
  sections: ['write'],
  carers: ['write'],
  carers_info: ['write'],
  teachers: ['none'],
  teachers_siso: ['none'],
  billing: ['none'],
  lead_crm: ['none'],
  lead_crm_states: ['read'],
  reports: ['none'],
  programs: ['read'],
  school_settings: ['none'],
  custom_attributes: ['read'],
  subscription: ['none'],
  teachers_messaging: ['read'],
  school_calendars: ['write'],
  parents_messaging: ['write'],
  documents: ['none'],
  accounting_total: ['none'],
  teachers_scheduling: ['none'],
  parent_relation: ['write'],
  newsletters: ['read'],
  subsidies: ['write'],
  meals: ['read'],
  assessments: ['read'],
  analytics: ['none'],
  newsletter_templates: ['write'],
  room_visibility: ['none'],
  center_te_reports: ['none'],
  corp_te_reports: ['none'],
  roles_and_permissions: ['read']
};
